import { useState, useCallback, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { get_deployment } from '../../../../API';
import { useChannel } from '../../../../Realtime';
import { useInterval } from '../../../../Util';
import { safeParse } from '../../../../Util';
import { getLatestInboundWhatsAppMessage, isWhatsAppWindowClosed, mapMessageKindToChannel } from './utils';
import type { ContactSelectorOption } from './types';
import type { SelectOptionType } from '@aidkitorg/component-library';

/**
 * User presence data for facepile
 */
export interface UserPresence {
  name: string;
  lastSeen: number;
  unsavedChanges?: boolean;
  browserTab?: string;
}

/**
 * Hook to track other users viewing the same case (facepile functionality)
 * @param caseId - The support case ID
 * @param currentUserName - The current user's name
 * @returns Array of other users currently viewing the case
 */
export const useOtherUsersViewing = (caseId: string, currentUserName: string) => {
  const [userList, setUserList] = useState<Record<string, UserPresence>>({});

  const channelName = get_deployment() + ':comms:' + caseId;

  const facePileCallback = useCallback((data: any) => {
    if (data.event === "heartbeat") {
      const userKey = data.data.name + '/' + data.data.browserTab;
      setUserList((lastSeen) => ({
        ...lastSeen,
        [userKey]: {
          lastSeen: Date.now(),
          ...data.data
        }
      }));
    }

    if (data.event === "remove_user") {
      const userKey = data.data.name + '/' + data.data.browserTab;
      setUserList((lastSeen) => {
        const newUsers = { ...lastSeen };
        delete newUsers[userKey];
        return newUsers;
      });
    }
  }, []);

  const sendData = useChannel({
    realm: 'facepile',
    name: channelName,
    onMessage: facePileCallback,
  });

  useEffect(() => {
    const handleBeforeUnload = () => {
      sendData({
        realm: 'facepile',
        channel: get_deployment() + ':comms:' + caseId,
        event: 'remove_user',
        data: {
          name: currentUserName,
          browserTab: 'Support Case'
        }
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sendData, currentUserName, caseId]);

  useInterval(() => {
    if (currentUserName && document.visibilityState === "visible") {
      sendData({
        realm: 'facepile',
        channel: channelName,
        event: 'heartbeat',
        data: {
          name: currentUserName,
          unsavedChanges: false,
          browserTab: 'Support Case'
        }
      });
    }
  }, 1000);

  // Show all users (including same user from other tabs) - matching old FacePile behavior
  const allUserKeys = Object.keys(userList);
  const now = Date.now();

  const otherUsers = allUserKeys
    .filter(k => {
      const timeSinceLastSeen = now - userList[k].lastSeen;
      const isActive = timeSinceLastSeen < 20000;
      return isActive; // Only filter by activity, not by user identity
    })
    .map((k) => userList[k]);

  return otherUsers;
};

/**
 * Hook to monitor WhatsApp 24-hour messaging window status
 * If the last inbound message from an applicant was more than 24 hours ago, the window is closed.
 * @param canSendWhatsApp - Whether WhatsApp can be sent at all
 * @param messages - Array of messages
 * @returns Whether the WhatsApp window is closed
 */
export const useWhatsAppWindow = (
  canSendWhatsApp: boolean,
  messages: any[]
): boolean => {
  const [whatsAppWindowClosed, setWhatsAppWindowClosed] = useState(false);

  useEffect(() => {
    if (!canSendWhatsApp) {
      setWhatsAppWindowClosed(false);
      return;
    }

    const latestInboundWhatsAppMessage = getLatestInboundWhatsAppMessage(messages);

    if (latestInboundWhatsAppMessage) {
      setWhatsAppWindowClosed(isWhatsAppWindowClosed(latestInboundWhatsAppMessage));
    } else {
      setWhatsAppWindowClosed(false);
    }
  }, [messages, canSendWhatsApp]);

  return whatsAppWindowClosed;
};

/**
 * Hook to manage contact selection state and options for alternate contacts
 * @param config - Configuration object with alternate_contact
 * @param contactInfo - Contact information dictionary
 * @param selectedChannel - Currently selected channel
 * @param contextLang - Current language
 * @returns Object with contact selector options and show flag
 */
export const useContactSelector = (
  config: any,
  contactInfo: any,
  selectedChannel: SelectOptionType | undefined,
  contextLang: string
) => {
  const [contactPhoneKey, setContactPhoneKey] = useState("phone_number");
  const [contactEmailKey, setContactEmailKey] = useState("email");
  const [contactNameKey, setContactNameKey] = useState("legal_name");

  const contactSelectorOptions: ContactSelectorOption[] = useMemo(() => {
    const alternateContacts = safeParse(config.alternate_contact || '[]', []);
    const currentChannel = selectedChannel?.value as 'email' | 'sms' | 'whatsapp' | undefined;

    return [
      {
        displayName: contactInfo?.['legal_name'] || 'Applicant',
        dropdownLabel: contactInfo?.['legal_name'] || 'Applicant',
        value: 'phone_number:email:legal_name',
        callback: () => {
          setContactPhoneKey("phone_number");
          setContactEmailKey("email");
          setContactNameKey("legal_name");
        },
        disabled: currentChannel === 'email'
          ? !contactInfo?.['email']
          : !contactInfo?.['phone_number'] && !contactInfo?.['phone']
      },
      ...alternateContacts
        .filter((ac: any) => contactInfo?.[ac.phone_key] || contactInfo?.[ac.email_key])
        .map((ac: {
          label: Record<string, string | undefined>,
          name_key: string,
          phone_key: string,
          email_key: string,
        }) => {
          const contactName = contactInfo?.[ac.name_key] || 'Unknown';
          const categoryLabel = ac.label[contextLang] || ac.label['en'];

          return {
            displayName: contactName,
            dropdownLabel: categoryLabel + ": " + contactName,
            value: `${ac.phone_key}:${ac.email_key}:${ac.name_key}`,
            callback: () => {
              setContactPhoneKey(ac.phone_key);
              setContactEmailKey(ac.email_key);
              setContactNameKey(ac.name_key);
            },
            disabled: currentChannel === 'email'
              ? !contactInfo?.[ac.email_key]
              : !contactInfo?.[ac.phone_key]
          };
        })
    ];
  }, [config.alternate_contact, contactInfo, contextLang, selectedChannel]);

  const showContactSelector = contactSelectorOptions.filter(opt => !opt.disabled).length > 1;

  // Reset to main contact when switching channels if current contact doesn't have the required field
  useEffect(() => {
    if (!selectedChannel) return;

    const currentChannel = selectedChannel.value as 'email' | 'sms' | 'whatsapp';
    const hasRequiredField = currentChannel === 'email'
      ? contactInfo?.[contactEmailKey]
      : contactInfo?.[contactPhoneKey];

    if (!hasRequiredField) {
      setContactPhoneKey("phone_number");
      setContactEmailKey("email");
      setContactNameKey("legal_name");
    }
  }, [selectedChannel, contactInfo, contactPhoneKey, contactEmailKey]);

  return {
    contactPhoneKey,
    contactEmailKey,
    contactNameKey,
    contactSelectorOptions,
    showContactSelector,
    setContactPhoneKey,
    setContactEmailKey,
    setContactNameKey
  };
};

/**
 * Hook to set initial channel based on most recent message type
 * @param messages - Array of messages
 * @param availableChannels - Array of available channels
 * @param L - Localization object
 * @returns Selected channel state and setter
 */
export const useInitialChannelSelection = (
  messages: any[],
  availableChannels: ('email' | 'sms' | 'whatsapp')[],
  L: any
) => {
  const [selectedChannel, setSelectedChannel] = useState<SelectOptionType | undefined>(undefined);
  const [hasSetInitialChannel, setHasSetInitialChannel] = useState(false);

  useEffect(() => {
    if (hasSetInitialChannel || messages.length === 0 || availableChannels.length === 0) {
      return;
    }

    const mostRecentMessage = messages[messages.length - 1];
    const preferredChannel = mapMessageKindToChannel(mostRecentMessage.kind);

    const channelToUse = (preferredChannel && availableChannels.includes(preferredChannel))
      ? preferredChannel
      : availableChannels[0];

    setSelectedChannel({
      label: channelToUse === 'email'
        ? L.applicant.comms.send_email
        : channelToUse === 'whatsapp'
          ? L.applicant.comms.send_whatsapp
          : L.applicant.comms.send_text,
      value: channelToUse,
    });

    setHasSetInitialChannel(true);
  }, [messages, availableChannels, hasSetInitialChannel, L.applicant.comms]);

  // Update selected channel when available channels change
  useEffect(() => {
    if (selectedChannel && !availableChannels.includes(selectedChannel.value as 'email' | 'sms' | 'whatsapp')) {
      const newChannel = availableChannels.length > 0 ? availableChannels[0] : 'sms';
      setSelectedChannel({
        label: newChannel === 'email'
          ? L.applicant.comms.send_email
          : newChannel === 'whatsapp'
            ? L.applicant.comms.send_whatsapp
            : L.applicant.comms.send_text,
        value: newChannel,
      });
    }
  }, [availableChannels, L.applicant.comms, selectedChannel]);

  return { selectedChannel, setSelectedChannel };
};

/**
 * Hook to show spinner only after a delay to prevent flashing on fast loads
 * @param isLoading - Whether content is loading
 * @param delayMs - Delay in milliseconds before showing spinner (default 300)
 * @returns Whether to show the spinner
 */
export const useDelayedSpinner = (
  isLoading: boolean | undefined,
  delayMs: number = 300
): boolean => {
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        setShowSpinner(true);
      }, delayMs);
      return () => clearTimeout(timer);
    } else {
      setShowSpinner(false);
    }
  }, [isLoading, delayMs]);

  return showSpinner;
};

/**
 * Hook to automatically scroll message container to bottom on new messages
 * @param messages - Array of messages
 * @param isLoading - Whether messages are loading
 * @returns Ref to attach to the scrollable container
 */
export const useAutoScroll = (
  messages: any[],
  isLoading: boolean | undefined
) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const previousMessageCountRef = useRef<number>(0);

  // Scroll to bottom when messages load or change
  useLayoutEffect(() => {
    if (messagesContainerRef.current && messages.length > 0 && !isLoading) {
      const container = messagesContainerRef.current;
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < 100;

      if (isNearBottom || container.scrollTop === 0) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [messages, isLoading]);

  // Force scroll to bottom when new message is sent
  useEffect(() => {
    if (messages.length > previousMessageCountRef.current && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
    previousMessageCountRef.current = messages.length;
  }, [messages.length]);

  return messagesContainerRef;
};
